import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { useTranslation } from "@motolynk/i18n";
import {
  Alert,
  Button,
  EmptyState,
  FormField,
  Icon,
  Input,
  Switch,
  Textarea,
  useConfirm,
} from "@motolynk/ui";

import { newFaq, type EditorValues } from "./values";

type RowErrors = Record<string, { message?: string }>;

/**
 * Questions and answers shown on the product page (plain text, in this order; hidden ones stay saved).
 * Removing asks first; the row goes when the product is saved.
 */
export function FaqsSection() {
  const { t } = useTranslation("products");
  const confirm = useConfirm();
  const { control, register, formState } = useFormContext<EditorValues>();
  const { fields, append, remove, move } = useFieldArray({ control, name: "faqs", keyName: "key" });
  const errors = formState.errors.faqs as
    (Record<number, RowErrors> & { message?: string; root?: { message?: string } }) | undefined;
  const rows = useWatch({ control, name: "faqs" });
  const rootError = errors?.message ?? errors?.root?.message;

  return (
    <div className="space-y-4">
      {rootError && <Alert tone="danger">{rootError}</Alert>}
      {fields.length === 0 ? (
        <EmptyState icon="help" title={t("editor.faqs.empty")} />
      ) : (
        <ul className="space-y-3">
          {fields.map((row, index) => {
            const rowErrors = errors?.[index];
            const name = rows?.[index]?.question.trim() || t("editor.faqs.row", { n: index + 1 });
            return (
              <li key={row.key} className="space-y-3 rounded-card border border-border p-3">
                <FormField
                  label={`${t("editor.faqs.question")} ${index + 1}`}
                  error={rowErrors?.question?.message ?? rowErrors?.uid?.message}
                  required
                >
                  <Input maxLength={300} {...register(`faqs.${index}.question`)} />
                </FormField>
                <FormField
                  label={`${t("editor.faqs.answer")} ${index + 1}`}
                  hint={t("editor.faqs.answerHint")}
                  error={rowErrors?.answer?.message}
                  required
                >
                  <Textarea rows={3} maxLength={2000} {...register(`faqs.${index}.answer`)} />
                </FormField>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <FormField label={t("editor.faqs.published")}>
                    <Controller
                      control={control}
                      name={`faqs.${index}.is_published`}
                      render={({ field }) => (
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      )}
                    />
                  </FormField>
                  <div className="flex gap-0.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      disabled={index === 0}
                      aria-label={t("editor.faqs.up", { name })}
                      onClick={() => move(index, index - 1)}
                    >
                      <Icon name="arrow-up" size={14} />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      disabled={index === fields.length - 1}
                      aria-label={t("editor.faqs.down", { name })}
                      onClick={() => move(index, index + 1)}
                    >
                      <Icon name="arrow-down" size={14} />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t("editor.faqs.remove", { name })}
                      onClick={() =>
                        confirm.ask({
                          title: t("editor.faqs.confirmTitle", { name }),
                          description: t("editor.faqs.confirmHint"),
                          confirmLabel: t("editor.faqs.removeShort"),
                          onConfirm: () => remove(index),
                        })
                      }
                    >
                      <Icon name="trash" size={14} />
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <Button type="button" variant="outline" size="sm" onClick={() => append(newFaq())}>
        <Icon name="plus" size={14} />
        {t("editor.faqs.add")}
      </Button>
      {confirm.dialog}
    </div>
  );
}
